import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2 } from 'lucide-react';
import { ProgressSpinner } from 'primereact/progressspinner';

export default function CardCobranca() {
  const [base, setBase] = useState(1);
  const [imageSrc, setImageSrc] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [sentItems, setSentItems] = useState([]);

  useEffect(() => {
    fetchConnection();
  }, []);

  useEffect(() => {
    if (isConnected) fetchList();
  }, [isConnected, base]);

  async function fetchConnection() {
    setConnecting(true);
    setError(null);
    setSuccessMsg('');
    setIsConnected(false);
    setImageSrc(null);
    try {
      const email = encodeURIComponent(localStorage.getItem('email'));
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(
        `https://selenium.lc-transportes.com/token/e/${email}/${token}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      const result = await resp.json();
      if (result.instance?.state === 'open') {
        setIsConnected(true);
      } else if (result.base64) {
        const b64 = result.base64.startsWith('data:')
          ? result.base64
          : `data:image/png;base64,${result.base64}`;
        setImageSrc(b64);
      }
    } catch (e) {
      setError(`Erro ao conectar: ${e.message}`);
    } finally {
      setConnecting(false);
    }
  }

  async function fetchList() {
    setLoadingList(true);
    setError(null);
    setSuccessMsg('');
    setList([]);
    try {
      const resp = await fetch('https://selenium.lc-transportes.com/token/d', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: null, senha: null, base, token: localStorage.getItem('accessToken') }),
      });
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      const data = await resp.json();
      setList(data);
    } catch (e) {
      setError(`Erro ao listar: ${e.message}`);
    } finally {
      setLoadingList(false);
    }
  }

  async function sendMessage(item) {
    setError(null);
    setSuccessMsg('');
    setSendingId(item.numero);
    try {
      const email = encodeURIComponent(localStorage.getItem('email'));
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(
        `https://selenium.lc-transportes.com/token/b/${email}/${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nome: item.nome, numero: item.numero, mensagemString: item.mensagemString }),
        }
      );
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      await resp.json();
      setSentItems(prev => [...prev, item.numero]);
      setSuccessMsg(`Mensagem para ${item.nome} enviada!`);
    } catch (e) {
      setError(`Erro ao enviar para ${item.nome}: ${e.message}`);
    } finally {
      setSendingId(null);
    }
  }

  return (
    <div className="relative space-y-6">
      {/* Conexão WhatsApp */}
      <div className="flex items-start bg-white rounded-lg shadow p-6">
        <div className="mr-6 flex-shrink-0">
          {isConnected ? (
            <div className="text-green-600 flex flex-col items-center">
              <CheckCircle2 className="w-6 h-6 mb-1" />
              <span className="font-semibold">Conectado</span>
            </div>
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt="QRCode"
              className="w-64 h-64 object-contain filter-grayscale-1 contrast-[1000%]"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 text-gray-500 flex items-center justify-center">
              QR indisponível
            </div>
          )}
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold text-gray-800">Conectar WhatsApp</h2>
          <p className="mt-2 text-gray-600">
            {isConnected
              ? 'Instância conectada. Pronto para enviar mensagens.'
              : 'Escaneie o QR Code com o WhatsApp para conectar.'}
          </p>
          <button
            onClick={fetchConnection}
            disabled={connecting}
            className={`mt-4 inline-flex items-center px-4 py-2 rounded text-white focus:outline-none ${
              connecting
                ? 'bg-lightBlue-600 cursor-wait'
                : 'bg-lightBlue-600 hover:bg-blue-700'
            }`}
          >
            {connecting && <RefreshCw className="w-5 h-5 mr-2 animate-spin" />}
            {connecting ? 'Conectando...' : isConnected ? 'Atualizar' : 'Recarregar'}
          </button>
        </div>
      </div>

      {/* Seletor de Base */}
      <div className="mb-4">
        <label htmlFor="baseSelect" className="block text-sm font-medium text-gray-700 mb-1">
          Selecione a base:
        </label>
        <select
          id="baseSelect"
          value={base}
          onChange={e => setBase(parseInt(e.target.value))}
          className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value={1}>Lc Transportes - FSA</option>
          <option value={2}>Lc Transportes - ALG</option>
          <option value={3}>Lc Transportes - SAJ</option>
          <option value={4}>Lc Transportes - JAC</option>
          <option value={5}>Lc Transportes - SSA</option>
          <option value={999}>TODAS AS BASES</option>
        </select>
      </div>

      {/* Loading overlay */}
      {loadingList && (
        <div className="fixed inset-0 bg-opacity-40 flex items-center justify-center z-50">
          <ProgressSpinner
            style={{ width: '50px', height: '50px' }}
            strokeWidth="8"
            fill="var(--surface-ground)"
            animationDuration=".5s"
          />
        </div>
      )}

      {error && <div className="p-2 bg-red-100 text-red-700 rounded">{error}</div>}
      {successMsg && <div className="p-2 bg-green-100 text-green-700 rounded">{successMsg}</div>}

      {/* Pacotes e Envio de Mensagens */}
      {isConnected && list.length > 0 && (
        <section className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">Pacotes</h3>
          <div className="divide-y">
            {list.map(item => {
              const sending = sendingId === item.numero;
              const sent = sentItems.includes(item.numero);
              return (
                <div key={item.numero} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-800">{item.nome}</p>
                    <p className="text-sm text-gray-500">{item.numero}</p>
                  </div>
                  <button
                    onClick={() => sendMessage(item)}
                    disabled={sent || sending}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded focus:outline-none ${
                      sent
                        ? 'bg-emerald-200 cursor-default text-white'
                        : sending
                        ? 'bg-emerald-200 cursor-wait text-white'
                        : 'bg-emerald-500 hover:bg-green-700 text-white'
                    }`}
                  >
                    {sending && (
                      <ProgressSpinner
                        style={{ width: '20px', height: '20px' }}
                        strokeWidth="8"
                        fill="var(--surface-ground)"
                        animationDuration=".5s"
                      />
                    )}
                    {sent ? 'Enviada' : sending ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
